import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../../context/CartContext';

const fetchSquareConfig = async (apiEndpoint) => {
  const response = await fetch(`${apiEndpoint}square-config`);
  if (!response.ok) {
    throw new Error('Failed to load payment configuration');
  }
  return response.json();
};

const loadSquareScript = (environment) => {
  return new Promise((resolve, reject) => {
    if (window.Square) {
      resolve(window.Square);
      return;
    }
    const src =
      environment === 'production'
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(window.Square);
    script.onerror = () => reject(new Error('Failed to load the Square payment form'));
    document.body.appendChild(script);
  });
};

const CheckoutModal = ({ onClose }) => {
  const { items, subtotal, clearCart } = useCart();
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

  const [status, setStatus] = useState('loading'); // loading | ready | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [contact, setContact] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [bookingResults, setBookingResults] = useState(null);

  const cardContainerRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (!apiEndpoint) {
          throw new Error('Payments are not configured yet');
        }
        const config = await fetchSquareConfig(apiEndpoint);
        const Square = await loadSquareScript(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.applicationId, config.locationId);
        const card = await payments.card();
        await card.attach(cardContainerRef.current);
        if (cancelled) {
          card.destroy();
          return;
        }
        cardRef.current = card;
        setStatus('ready');
      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(error.message || 'Could not load the payment form');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        cardRef.current.destroy();
        cardRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint]);

  const handleContactChange = (field) => (event) => {
    setContact((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!cardRef.current || status === 'submitting') {
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    let nonce;
    let orderId;
    try {
      const tokenizeResult = await cardRef.current.tokenize();
      if (tokenizeResult.status !== 'OK') {
        throw new Error(tokenizeResult.errors?.[0]?.message || 'Card was declined');
      }
      nonce = tokenizeResult.token;

      const orderItems = items.map((line) => ({
        variationId: line.variationId,
        note: line.kind === 'lesson' ? line.meta : undefined,
      }));

      const orderResponse = await fetch(`${apiEndpoint}orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: orderItems }),
      });
      const orderData = await orderResponse.json();
      if (!orderResponse.ok) {
        throw new Error(orderData.error || 'Could not create your order');
      }
      orderId = orderData.orderId;

      const paymentResponse = await fetch(`${apiEndpoint}payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nonce, orderId, buyerEmail: contact.email }),
      });
      const paymentData = await paymentResponse.json();
      if (!paymentResponse.ok) {
        throw new Error(paymentData.error || 'Payment was not successful');
      }
    } catch (error) {
      // Nothing was charged yet at any point this can throw - safe to show
      // a plain error and let the customer retry.
      setStatus('error');
      setErrorMessage(error.message || 'Something went wrong');
      return;
    }

    // Payment has been captured. From here on we never fall back to the
    // generic error screen - if booking creation fails, the customer still
    // paid, so we show success and flag which bookings need manual
    // follow-up instead (the order's line-item notes have the details).
    const lessonLines = items.filter((line) => line.kind === 'lesson');
    let results = [];
    if (lessonLines.length > 0) {
      try {
        const bookingsResponse = await fetch(`${apiEndpoint}bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer: {
              email: contact.email,
              firstName: contact.firstName,
              lastName: contact.lastName,
              phone: contact.phone,
            },
            bookings: lessonLines.map((line) => ({
              serviceVariationId: line.variationId,
              serviceVariationVersion: line.variationVersion,
              teamMemberId: line.teamMemberId,
              startAt: line.startAt,
              durationMinutes: line.durationMinutes,
              partySize: line.partySize,
            })),
          }),
        });
        const bookingsData = await bookingsResponse.json();
        results = bookingsData.results ?? lessonLines.map((line) => ({ success: false, startAt: line.startAt }));
      } catch {
        results = lessonLines.map((line) => ({ success: false, startAt: line.startAt }));
      }
    }

    setBookingResults(results);
    setStatus('success');
    clearCart();
  };

  const failedBookings = bookingResults?.filter((result) => !result.success) ?? [];

  return (
    <div className="fixed inset-0 bg-walnut-900/40 z-[60] flex items-center justify-center p-4">
      <div className="bg-linen-50 rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-2xl mx-auto mb-4">
              &#10003;
            </div>
            <h3 className="font-display text-xl font-semibold text-charcoal-900 mb-2">Payment successful</h3>
            <p className="text-walnut-600 text-sm mb-4">
              Thanks! A confirmation will be sent to {contact.email || 'your email'}.
            </p>
            {failedBookings.length > 0 && (
              <p className="text-sm text-copper-700 bg-copper-50 border border-copper-200 rounded-lg p-3 mb-4">
                Your payment went through, but we couldn't automatically confirm{' '}
                {failedBookings.length === 1 ? 'one of your lesson bookings' : 'some of your lesson bookings'}.
                We'll follow up by email to lock in the time.
              </p>
            )}
            <button
              onClick={onClose}
              className="w-full bg-copper-500 text-linen-50 px-4 py-3 rounded-lg font-medium hover:bg-copper-600 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold text-charcoal-900">Checkout</h3>
              <button onClick={onClose} className="text-2xl text-walnut-500 hover:text-walnut-700" aria-label="Close">
                &times;
              </button>
            </div>

            <div className="bg-linen-100 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between font-semibold text-charcoal-900">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-walnut-500 text-xs mt-1">Tax, if any, is calculated by Square at checkout.</p>
            </div>

            {status === 'error' && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-walnut-600 mb-1">First name</label>
                  <input
                    type="text"
                    required
                    value={contact.firstName}
                    onChange={handleContactChange('firstName')}
                    className="w-full border border-linen-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-walnut-600 mb-1">Last name</label>
                  <input
                    type="text"
                    required
                    value={contact.lastName}
                    onChange={handleContactChange('lastName')}
                    className="w-full border border-linen-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-walnut-600 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={contact.email}
                  onChange={handleContactChange('email')}
                  className="w-full border border-linen-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-walnut-600 mb-1">Phone</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={handleContactChange('phone')}
                  className="w-full border border-linen-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <label className="block text-xs font-semibold text-walnut-600 mb-1">Card details</label>
              {status === 'loading' && (
                <div className="text-walnut-400 text-sm py-4 text-center border border-linen-200 rounded-lg mb-4">
                  Loading payment form...
                </div>
              )}
              {/* Square's SDK attaches its own iframe into this node imperatively -
                  it must stay mounted continuously (never removed by a status check)
                  and React must never render children into it, or the two DOM
                  owners fight each other on the next render. */}
              <div
                ref={cardContainerRef}
                className={`border border-linen-200 rounded-lg p-3 mb-4 min-h-[56px] ${
                  status === 'loading' ? 'hidden' : ''
                }`}
              />

              <button
                type="submit"
                disabled={status !== 'ready'}
                className="w-full bg-copper-500 text-linen-50 px-4 py-3 rounded-lg font-medium hover:bg-copper-600 transition-colors disabled:bg-copper-300 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Processing...' : `Pay $${subtotal.toFixed(2)}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
